import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Heart, Clock, DollarSign, CheckCircle, Star, Sparkles } from "lucide-react";

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const endOfLifeDuties = [
    "Emotional support and companionship for the dying person",
    "Comfort measures including positioning, cooling, and presence",
    "Creating a peaceful, calming environment",
    "Playing meaningful music or reading favorite passages",
    "Facilitating conversations between family members and the dying",
    "Helping family members understand the dying process",
    "Providing respite care so family can rest",
    "Guiding family through difficult emotions and decisions",
    "Supporting vigil planning and coordination",
    "Legacy work and memory-making activities",
    "Assisting with advance directive discussions",
    "Being a calm, steady presence during the final hours"
  ];

  const postMortemDuties = [
    "Respectful washing and preparation of the body",
    "Dressing the deceased in chosen clothing",
    "Positioning and presentation for family viewing",
    "Coordinating with funeral home for transport",
    "Supporting family during the immediate aftermath",
    "Guiding family through next steps and paperwork",
    "Creating a peaceful environment for final goodbyes",
    "Documenting any personal effects as requested",
    "Providing emotional support during this sacred time"
  ];

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      id="services" 
      className="relative py-24 md:py-32 bg-[#E8E4D9]"
      ref={ref}
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4">
            How I Can Help
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#2D2A26] mb-6">
            Services & Pricing
          </h2>
          <p className="text-lg text-[#5C5852] max-w-2xl mx-auto">
            Compassionate, professional support tailored to your family's unique needs 
            during life's most sacred transition.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* End of Life Care */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.08)] border border-[#4A5D23]/10"
            data-testid="service-end-of-life"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#4A5D23] rounded-2xl flex items-center justify-center">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26]">
                  End of Life Care
                </h3>
                <p className="text-[#8F9E82] text-sm">Compassionate Companionship</p>
              </div>
            </div>

            <div className="bg-[#F9F8F4] rounded-2xl p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#4A5D23]" />
                  <span className="text-3xl font-semibold text-[#2D2A26]">$75</span>
                  <span className="text-[#5C5852]">/ hour</span>
                </div>
                <div className="text-center sm:text-right">
                  <p className="text-[#5C5852] text-sm">Or</p>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#4A5D23]" />
                  <span className="text-3xl font-semibold text-[#2D2A26]">$200</span>
                  <span className="text-[#5C5852]">/ 4 hours min</span>
                </div>
              </div>
            </div>

            <p className="text-[#5C5852] mb-6 leading-relaxed">
              Dedicated support for your loved one and family during the final stage of life. 
              I provide continuous, compassionate presence to ensure comfort, dignity, and peace.
            </p>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-[#4A5D23] uppercase tracking-wider">
                What's Included:
              </p>
              <ul className="grid grid-cols-1 gap-2">
                {endOfLifeDuties.map((duty, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-[#5C5852]">
                    <CheckCircle className="w-4 h-4 text-[#8F9E82] flex-shrink-0 mt-0.5" />
                    <span>{duty}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Post Mortem Care */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.08)] border border-[#4A5D23]/10"
            data-testid="service-post-mortem"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#8F9E82] rounded-2xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26]">
                  Post Mortem Care
                </h3>
                <p className="text-[#8F9E82] text-sm">Sacred Body Care</p>
              </div>
            </div>

            <div className="bg-[#F9F8F4] rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#4A5D23]" />
                <span className="text-3xl font-semibold text-[#2D2A26]">$400</span>
                <span className="text-[#5C5852]">flat fee</span>
              </div>
            </div>

            <p className="text-[#5C5852] mb-6 leading-relaxed">
              Respectful and dignified care of your loved one after passing. This sacred service 
              allows families time to say goodbye while ensuring your loved one is treated with 
              the utmost care and reverence.
            </p>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-[#4A5D23] uppercase tracking-wider">
                What's Included:
              </p>
              <ul className="grid grid-cols-1 gap-2">
                {postMortemDuties.map((duty, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-[#5C5852]">
                    <CheckCircle className="w-4 h-4 text-[#8F9E82] flex-shrink-0 mt-0.5" />
                    <span>{duty}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Consultation Card */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-[#4A5D23] rounded-3xl p-8 md:p-12 text-white"
          data-testid="service-consultation"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#E8E4D9]" />
                <span className="text-sm uppercase tracking-widest text-[#E8E4D9] font-bold">
                  Start Here
                </span>
              </div>
              <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl mb-4">
                Initial Consultation
              </h3>
              <p className="text-[#E8E4D9] leading-relaxed mb-6">
                Begin your journey with a compassionate, in-depth consultation where we discuss 
                your family's unique needs, answer your questions, and create a personalized 
                care plan together.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-[#E8E4D9]">
                  <CheckCircle className="w-4 h-4" />
                  60-minute comprehensive session
                </li>
                <li className="flex items-center gap-2 text-[#E8E4D9]">
                  <CheckCircle className="w-4 h-4" />
                  Personalized care planning
                </li>
                <li className="flex items-center gap-2 text-[#E8E4D9]">
                  <CheckCircle className="w-4 h-4" />
                  Q&A about end-of-life process
                </li>
              </ul>
            </div>
            <div className="text-center md:text-right">
              <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-6">
                <p className="text-5xl font-semibold mb-2">$100</p>
                <p className="text-[#E8E4D9]">for 60 minutes</p>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm text-[#E8E4D9]">
                    <span className="font-semibold text-white">$25 deposit</span> required to book
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="bg-white text-[#4A5D23] px-8 py-4 rounded-full font-semibold w-full md:w-auto hover:bg-[#F9F8F4] transition-colors"
                data-testid="book-consultation-btn"
              >
                Book Your Consultation
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
